/**
 * The Loading vs Capacity surface's state and data, in one place.
 *
 * SIX KEYS LIVE IN THE URL so a view is linkable — `?site=Kirkstall&resource=MATSURA&
 * week=3&view=levelled&weeks=26`. `site` is the app's existing scope grammar (useScope, CD-8) rather
 * than a second `scope` param: the top bar's scope controls write it, the design's SCOPE chips write it,
 * and two controls disagreeing about which entity you are looking at is worse than either
 * being missing. Changing site clears the open resource — the old resource belongs to the
 * old entity.
 *
 * ONE REQUEST PER ENTITY, ALWAYS `mode=levelled`. The payload carries BOTH readings of the
 * same hours (models/loading.py), and the surface needs both at once: the clearance lanes,
 * the KPIs and the clears badges are levelled even while the charts are drawn as scheduled.
 * Asking for as_scheduled would serve half the page and re-fetch on every toggle.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLoadingAll, type LoadingEntity } from './useLoading'
import { withSite } from './useScope'
import type { Site } from '../types/metrics'
import {
  byEntityThenLoad, kpisOf, processOf, resourcesOf,
  type LoadingKpis, type ProcessGroup, type ResourceModel,
} from '../viewmodels/loading'

/**
 * The horizons the surface will ask for, in weeks. 13 is the window the 24 Jul review agreed
 * and stays what the page opens on.
 *
 * IT IS A CHOICE SINCE 31 AUG 2026 (Mike). The book does not stop at week 13, and a fixed
 * horizon reports the work beyond it as ABSENT rather than as later: 9,029 h of Kirkstall's
 * 28,682 h of served forward load — 31% — falls outside 13 weeks, against 193 h of Oracle's
 * 12,642 h. One window cannot read both estates honestly. 52 is the API's own ceiling
 * (WeeksParam in routers/loading.py) and holds 100% of both books.
 */
export const HORIZONS = [13, 26, 52] as const
export type Horizon = (typeof HORIZONS)[number]

/** What the page opens on, and what an absent or unknown `weeks` falls back to. */
export const DEFAULT_HORIZON: Horizon = 13

/** The URL's `weeks`, validated against the offered set. An arbitrary integer is NOT
 *  honoured: the horizon rides in the query key of every request this page makes, so a
 *  hand-typed value would fragment the cache and put a window on screen that no control on
 *  the page can name or leave. */
const horizonOf = (raw: string | null): Horizon =>
  HORIZONS.find((weeks) => weeks === Number(raw)) ?? DEFAULT_HORIZON

/**
 * THE DENOMINATOR IS NOT DECIDED HERE ANY MORE (19 Aug 2026).
 *
 * `CAPACITY_BASIS = 'calendar_24h'` used to live on this line. It was the single most
 * consequential number on the page — which hours every percentage a client reads is divided
 * by — and it sat in a React module where no governance process could reach it, while the
 * file that exists to govern exactly that (registry/capacity_basis.yaml) said nothing about
 * it. The handoff was right that a reader should not pick the basis; it does not follow that
 * a frontend constant should.
 *
 * The server now answers. Requests omit `basis`, the API defaults to the governed headline,
 * and the response says which one it used — so the grid and the trailing-utilisation card
 * cannot name different denominators, which two separately-authored constants always
 * eventually do (G25 is the same failure one layer down).
 */
export const HEADLINE_BASIS_FALLBACK = 'calendar_24h'

/** Site → the load mart's entity key. Group is every one of them. */
export const LOAD_SITES: { site: Site; entity: string }[] = [
  { site: 'Kirkstall', entity: 'kirkstall' },
  { site: 'Oracle', entity: 'oracle' },
]

const entityToSite = (entity: string): Site =>
  LOAD_SITES.find((s) => s.entity === entity)?.site ?? 'Kirkstall'

export type LoadingView = 'levelled' | 'scheduled'

export interface SurfaceState {
  view: LoadingView
  /** How many weeks forward the page asks for. See HORIZONS. */
  horizon: Horizon
  resource: string | null // `${entity}:${resource_group}`
  week: number
  /** The works order whose route is open beneath the week — FM's key, suffix included. */
  wo: string | null
  setView: (v: LoadingView) => void
  /**
   * Change the window. It drops the open works order for the same reason setWeek does:
   * shortening the horizon can move the selected week — it is clamped on read — and an open
   * route belongs to the week it was opened from. The resource stays, because a resource
   * belongs to the entity and not to the window.
   */
  setHorizon: (weeks: Horizon) => void
  openResource: (id: string, week: number) => void
  closeResource: () => void
  setWeek: (w: number) => void
  openWorksOrder: (wo: string | null) => void
  /**
   * Change scope AND leave the open resource in ONE write.
   *
   * It has to be one. `site` belongs to useScope and `resource`/`week` belong here, so two
   * `useSearchParams` setters fire in the same handler — and each reads its own snapshot of
   * the query string, so the second silently discards the first. The Capacity breadcrumb
   * cleared the resource, the site write put it straight back, and the crumb did nothing.
   */
  goToSite: (site: Site) => void
}

/** Everything an open resource owns. Cleared together whenever the reader leaves it. */
const RESOURCE_KEYS = ['resource', 'week', 'wo'] as const

type Setters = Pick<SurfaceState,
  'setView' | 'setHorizon' | 'openResource' | 'closeResource' | 'setWeek' | 'openWorksOrder' | 'goToSite'>

/** The writers, split from the readers to hold the CD-1 function budget in useSurfaceState. */
function useSurfaceSetters(setParams: ReturnType<typeof useSearchParams>[1]): Setters {
  const write = useCallback(
    (mutate: (p: URLSearchParams) => void) =>
      setParams((previous) => {
        const next = new URLSearchParams(previous)
        mutate(next)
        return next
      }),
    [setParams]
  )
  return {
    setView: useCallback((v) => write((p) => p.set('view', v)), [write]),
    setHorizon: useCallback(
      (weeks) => write((p) => { p.set('weeks', String(weeks)); p.delete('wo') }),
      [write]
    ),
    openResource: useCallback(
      (id, week) => write((p) => {
        p.set('resource', id)
        p.set('week', String(week))
        p.delete('wo') // an open route belongs to the week it was opened from
      }),
      [write]
    ),
    closeResource: useCallback(
      () => write((p) => RESOURCE_KEYS.forEach((key) => p.delete(key))),
      [write]
    ),
    setWeek: useCallback(
      (w) => write((p) => { p.set('week', String(w)); p.delete('wo') }),
      [write]
    ),
    openWorksOrder: useCallback(
      (wo) => write((p) => (wo === null ? p.delete('wo') : p.set('wo', wo))),
      [write]
    ),
    goToSite: useCallback((site: Site) => setParams((p) => leaveFor(p, site)), [setParams]),
  }
}

/** Scope change: the site grammar from useScope, plus everything the old scope owned. */
const leaveFor = (previous: URLSearchParams, site: Site): URLSearchParams => {
  const next = withSite(new URLSearchParams(previous), site)
  RESOURCE_KEYS.forEach((key) => next.delete(key))
  return next
}

/** The five surface-local URL keys. `site` is not one of them — useScope owns it. */
export function useSurfaceState(): SurfaceState {
  const [params, setParams] = useSearchParams()
  const raw = Number(params.get('week'))
  const horizon = horizonOf(params.get('weeks'))
  return {
    view: params.get('view') === 'scheduled' ? 'scheduled' : 'levelled',
    horizon,
    resource: params.get('resource'),
    week: Number.isInteger(raw) ? Math.min(horizon - 1, Math.max(0, raw)) : 0,
    wo: params.get('wo'),
    ...useSurfaceSetters(setParams),
  }
}

/** An entity in scope with no scheduled-load model, and the backend's own words for why. */
export interface UnservedSite {
  site: Site
  reason: string
}

export interface SurfaceData {
  isLoading: boolean
  entities: LoadingEntity[]
  /** The entities in scope that have no scheduled-load model — named, never dropped. */
  unserved: UnservedSite[]
  resources: ResourceModel[]
  process: ProcessGroup[]
  processHours: number
  weeks: string[]
  kpis: LoadingKpis
  /** The denominator the SERVER used, echoed back — never a second constant here. Falls
   *  back only when no entity in scope served a view at all, and then nothing divides by
   *  it anyway. */
  basis: string
}

/** Everything in scope, merged. Group = every entity that serves load, plus the names of
 *  the ones that do not, so a Group total can never quietly mean one site. */
export function useSurfaceData(site: Site, horizon: Horizon): SurfaceData {
  const inScope = useMemo(
    () => (site === 'Group' ? LOAD_SITES : LOAD_SITES.filter((s) => s.site === site)),
    [site]
  )
  // basis omitted: the server applies the governed headline (registry/capacity_basis.yaml).
  const entities = useLoadingAll(inScope.map((s) => s.entity), horizon, null, 'levelled')
  return useMemo(() => {
    const served = entities.filter((e) => e.view)
    const resources = served.flatMap((e) => resourcesOf(e.view!, entityToSite(e.entity)))
    const process = served.flatMap((e) => processOf(e.view!, entityToSite(e.entity)))
    return {
      isLoading: entities.some((e) => e.isLoading),
      entities,
      unserved: entities.filter((e) => !e.view && !e.isLoading).map((e) => ({
        site: entityToSite(e.entity),
        // The typed refusal's DETAIL, not its reason code: "no scheduled load for entity
        // 'oracle'" is a sentence a planner can act on; "unserved_entity" is not.
        reason: (e.error?.body?.detail as string | undefined) ?? e.error?.message ?? 'no scheduled load served',
      })),
      resources: resources.sort(byEntityThenLoad),
      process,
      processHours: process.reduce((a, p) => a + p.hours, 0),
      weeks: served[0]?.view?.weeks ?? [],
      kpis: kpisOf(resources),
      basis: served[0]?.view?.basis ?? HEADLINE_BASIS_FALLBACK,
    }
  }, [entities])
}
